// ─── AutoChinBot · Task Dashboard — Pending Orders ─────────────
// Shows the live "Vào lệnh chờ" (pending) orders for the selected cTrader
// account, plus per-row EDIT / DELETE buttons and a global PAUSE banner.
//
// EDIT opens a small dialog to change the price / volume / validity. The row is
// reset to pending_new server-side so the background engine re-validates the new
// price against the live market before arming it again.
//
// The pause banner appears when task5's risk gate blocked a pending fire:
// ALL pending orders are put on hold until an admin presses "Bật lại" here.

import { useState } from 'react';
import type { AutoChinPendingOrder } from '@/lib/autochin-pending-controller';
import { cn } from './types';

interface AutoChinPendingOrdersProps {
  orders: AutoChinPendingOrder[];
  paused: boolean;
  pauseReason: string;
  onDelete: (id: string) => void;
  onEdit: (id: string, patch: { price: number; volume: number; hours: number }) => Promise<{ success: boolean; error?: string }>;
  onResume: () => void;
}

function ctxNum(o: AutoChinPendingOrder, key: string): number | null {
  const raw = (o.signal_context as Record<string, unknown> | null)?.[key];
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function fmtPrice(n: number | null): string {
  if (n == null) return '—';
  return n.toFixed(5).replace(/0+$/, '').replace(/\.$/, '');
}

function fmtRemaining(expiresAt: string | null): { text: string; urgent: boolean } {
  if (!expiresAt) return { text: '—', urgent: false };
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return { text: 'hết hạn', urgent: true };
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return { text: h > 0 ? `${h}h ${m}m` : `${m}m`, urgent: ms < 10 * 60000 };
}

const STATUS_STYLE: Record<string, { text: string; label: string; cls: string }> = {
  pending_new: { text: 'text-amber-500', label: 'Đang kiểm tra giá', cls: 'bg-amber-500/10 text-amber-500' },
  pending_armed: { text: 'text-emerald-500', label: 'Đang chờ khớp', cls: 'bg-emerald-500/10 text-emerald-500' },
};

const DECIMAL_RE = /^\d+(\.\d+)?$/;
const INT_RE = /^\d+$/;

export default function AutoChinPendingOrders({
  orders,
  paused,
  pauseReason,
  onDelete,
  onEdit,
  onResume,
}: AutoChinPendingOrdersProps) {
  const [editing, setEditing] = useState<AutoChinPendingOrder | null>(null);
  const [priceInput, setPriceInput] = useState('');
  const [volumeInput, setVolumeInput] = useState('');
  const [hoursInput, setHoursInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  function openEdit(o: AutoChinPendingOrder) {
    setEditing(o);
    setPriceInput(ctxNum(o, 'price') != null ? String(ctxNum(o, 'price')) : '');
    setVolumeInput(o.volume != null ? String(o.volume) : '');
    setHoursInput(ctxNum(o, 'hours') != null ? String(ctxNum(o, 'hours')) : '');
    setEditError('');
    setSaving(false);
  }

  function closeEdit() {
    if (saving) return;
    setEditing(null);
    setEditError('');
  }

  async function submitEdit() {
    if (!editing) return;
    const price = priceInput.trim();
    const volume = volumeInput.trim();
    const hours = hoursInput.trim();

    if (!DECIMAL_RE.test(price) || Number(price) <= 0) {
      setEditError('Giá vào phải là số dương (vd 1.0850).');
      return;
    }
    if (!DECIMAL_RE.test(volume) || Number(volume) <= 0) {
      setEditError('Volume phải là số dương (vd 0.1).');
      return;
    }
    if (!INT_RE.test(hours) || Number(hours) <= 0) {
      setEditError('Hiệu lực phải là số nguyên dương (giờ).');
      return;
    }

    setSaving(true);
    setEditError('');
    const res = await onEdit(editing.id, {
      price: Number(price),
      volume: Number(volume),
      hours: Number(hours),
    });
    if (res.success) {
      setSaving(false);
      setEditing(null);
    } else {
      setSaving(false);
      setEditError(res.error || 'Không cập nhật được lệnh chờ.');
    }
  }

  return (
    <div className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3.5 border-b border-background-200/70 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-accent-500/10 flex items-center justify-center">
            <i className="ri-timer-flash-line text-accent-500 text-sm"></i>
          </div>
          <div>
            <h3 className="text-sm font-heading font-semibold text-foreground-900">Lệnh chờ</h3>
            <p className="text-[11px] text-foreground-500">
              Theo dõi thị trường tick-by-tick để khớp khi giá chạm điểm vào lệnh
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium bg-secondary-500/10 text-secondary-600">
            <i className="ri-list-check-2 text-[12px]"></i>
            {orders.length} lệnh chờ
          </span>
          {paused && (
            <button
              type="button"
              onClick={onResume}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-emerald-500 text-background-50 hover:bg-emerald-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line text-[13px]"></i>
              Bật lại
            </button>
          )}
        </div>
      </div>

      {/* Pause banner */}
      {paused && (
        <div className="px-5 py-3 bg-amber-500/10 border-b border-amber-500/20 flex items-start gap-3">
          <div className="w-7 h-7 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
            <i className="ri-pause-circle-line text-amber-500 text-sm"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-heading font-semibold text-amber-600">Đã tạm dừng toàn bộ lệnh chờ</p>
            <p className="text-[11px] text-foreground-600 break-words">
              {pauseReason || 'Điều kiện kiểm soát rủi ro (task5) đang chặn lệnh.'} — bấm <b>Bật lại</b> để tiếp tục theo dõi.
            </p>
          </div>
        </div>
      )}

      {/* Table / empty */}
      {orders.length === 0 ? (
        <div className="px-5 py-12 text-center">
          <div className="w-12 h-12 rounded-2xl bg-background-200/70 flex items-center justify-center mx-auto mb-3">
            <i className="ri-timer-flash-line text-foreground-300 text-xl"></i>
          </div>
          <p className="text-xs text-foreground-500">
            Chưa có lệnh chờ nào. Dùng nút <b>Vào lệnh chờ</b> trong hội thoại Telegram để tạo.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200/70 bg-background-50">
                <th className="text-left px-5 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">Symbol</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">Hướng</th>
                <th className="text-right px-4 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">Giá vào</th>
                <th className="text-right px-4 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">Volume</th>
                <th className="text-center px-4 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">Max</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">Hiệu lực</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">Trạng thái</th>
                <th className="text-right px-5 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const price = ctxNum(o, 'price');
                const maxOrders = ctxNum(o, 'max_orders');
                const side = String(o.trade_side || '').toUpperCase();
                const st = STATUS_STYLE[o.status] || { text: 'text-foreground-500', label: o.status, cls: 'bg-background-200/70 text-foreground-500' };
                const rem = fmtRemaining(o.expires_at);
                return (
                  <tr key={o.id} className="border-b border-background-200/40 last:border-b-0 hover:bg-background-50/60 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', paused ? 'bg-amber-400' : 'bg-accent-500/70')}></span>
                        <span className="text-sm font-heading font-semibold text-foreground-900">{o.symbol || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold',
                        side === 'BUY' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-400',
                      )}>
                        <i className={cn('text-[11px]', side === 'BUY' ? 'ri-arrow-up-line' : 'ri-arrow-down-line')}></i>
                        {side || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-mono text-foreground-800">{fmtPrice(price)}</td>
                    <td className="px-4 py-3 text-right text-sm font-mono text-foreground-800">{o.volume ?? '—'}</td>
                    <td className="px-4 py-3 text-center text-xs text-foreground-600">{maxOrders ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span className={cn('inline-flex items-center gap-1 text-[11px] font-medium', rem.urgent ? 'text-rose-400' : 'text-foreground-500')}>
                        <i className="ri-time-line text-[12px]"></i>
                        {rem.text}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap', st.cls)}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse"></span>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(o)}
                          title="Sửa lệnh chờ"
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-background-300/60 text-foreground-500 hover:bg-secondary-500/10 hover:text-secondary-600 hover:border-secondary-500/30 transition-colors cursor-pointer"
                        >
                          <i className="ri-pencil-line text-[14px]"></i>
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(o.id)}
                          title="Xoá lệnh chờ"
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-background-300/60 text-foreground-500 hover:bg-rose-500/10 hover:text-rose-400 hover:border-rose-500/30 transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-6-line text-[14px]"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Edit dialog ─── */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={closeEdit}
            aria-hidden="true"
          ></div>
          <div className="relative w-full max-w-md bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-secondary-500/10 flex items-center justify-center">
                  <i className="ri-pencil-line text-secondary-600 text-sm"></i>
                </div>
                <div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-900">Sửa lệnh chờ</h3>
                  <p className="text-[11px] text-foreground-500">
                    {editing.symbol || '—'} • <span className={cn(String(editing.trade_side || '').toUpperCase() === 'BUY' ? 'text-emerald-500' : 'text-rose-400')}>{String(editing.trade_side || '').toUpperCase() || '—'}</span>
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeEdit}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-foreground-500 hover:bg-background-200/60 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-[18px]"></i>
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5" htmlFor="pend-price">Giá vào (price)</label>
                <input
                  id="pend-price"
                  type="text"
                  inputMode="decimal"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  placeholder="vd 1.0850"
                  className="w-full px-3 py-2 rounded-lg bg-background-100 border border-background-300/60 text-sm font-mono text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500/50"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5" htmlFor="pend-volume">Volume (lots)</label>
                  <input
                    id="pend-volume"
                    type="text"
                    inputMode="decimal"
                    value={volumeInput}
                    onChange={(e) => setVolumeInput(e.target.value)}
                    placeholder="vd 0.1"
                    className="w-full px-3 py-2 rounded-lg bg-background-100 border border-background-300/60 text-sm font-mono text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5" htmlFor="pend-hours">Hiệu lực (giờ)</label>
                  <input
                    id="pend-hours"
                    type="text"
                    inputMode="numeric"
                    value={hoursInput}
                    onChange={(e) => setHoursInput(e.target.value)}
                    placeholder="vd 4"
                    className="w-full px-3 py-2 rounded-lg bg-background-100 border border-background-300/60 text-sm font-mono text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500/50"
                  />
                </div>
              </div>

              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-accent-500/10 border border-accent-500/20">
                <div className="w-4 h-4 mt-0.5 flex items-center justify-center shrink-0">
                  <i className="ri-information-line text-accent-600 text-[13px]"></i>
                </div>
                <p className="text-[11px] text-foreground-600 leading-snug">
                  Hệ thống sẽ kiểm tra lại giá so với thị trường: <b>BUY</b> phải thấp hơn giá hiện tại, <b>SELL</b> phải cao hơn. Hiệu lực được tính lại từ lúc lưu.
                </p>
              </div>

              {editError && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose-500/10 border border-rose-500/20">
                  <div className="w-4 h-4 mt-0.5 flex items-center justify-center shrink-0">
                    <i className="ri-error-warning-line text-rose-400 text-[13px]"></i>
                  </div>
                  <p className="text-[11px] text-rose-400 leading-snug">{editError}</p>
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-background-200/70 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeEdit}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium border border-background-300/60 text-foreground-600 hover:bg-background-200/60 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                Huỷ
              </button>
              <button
                type="button"
                onClick={submitEdit}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {saving ? (
                  <span className="w-3.5 h-3.5 border-2 border-background-50 border-t-transparent rounded-full animate-spin"></span>
                ) : (
                  <i className="ri-save-line text-[13px]"></i>
                )}
                Lưu thay đổi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}